import { isValidEmail } from '@helpers/strings'
import type { TextFieldProps } from '@mui/material'
import Chip from '@mui/material/Chip'
import Downshift, { type ControllerStateAndHelpers } from 'downshift'
import React, {
	type ComponentProps,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react'

import * as S from './styles'

interface IChipsInputpProps {
	chips: readonly string[]
	selectedChips: (items: readonly string[]) => void
	placeholder?: string
	variant?: TextFieldProps['variant']
	id?: string
	name?: string
	label?: string
	emailValidation: boolean
	onValueChange?: (value: string) => void
	invalidEmailLabel?: string
}

const DeleteChip = <T,>({
	value,
	onDelete,
	...props
}: { value: T; onDelete: (value: T) => void } & Omit<
	ComponentProps<typeof Chip>,
	'onDelete'
>) => {
	const handleDelete = useCallback(() => onDelete(value), [onDelete, value])
	return <Chip {...props} onDelete={handleDelete} />
}

const ChipsInputContainerWithEvents = ({
	inputProps,
	onChange,
	onInputPropsChange,
	selectedItems,
	onDelete,
	onBlur,
	...props
}: Omit<
	ComponentProps<typeof S.ChipsInputContainer>,
	'InputProps' | 'onChange'
> & {
	selectedItems: readonly string[]
	onDelete: (item: string) => void
	onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
	onInputPropsChange: (
		event: React.ChangeEvent<HTMLInputElement>,
		onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
	) => void
}) => {
	const InputProps = useMemo(
		() => ({
			startAdornment: (selectedItems || []).map(item => (
				<DeleteChip
					key={item}
					tabIndex={-1}
					label={item}
					onDelete={onDelete}
					value={item}
				/>
			)),
			onBlur,
			onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
				onInputPropsChange(event, onChange),
		}),
		[onInputPropsChange, onChange, onDelete, onBlur, selectedItems]
	)
	const completeInputProps = useMemo(
		() => ({
			'data-testid': 'invite-email',
			...inputProps,
		}),
		[inputProps]
	)
	return (
		<S.ChipsInputContainer
			{...props}
			InputProps={InputProps}
			inputProps={completeInputProps}
		/>
	)
}

const ChipsInput = (props: IChipsInputpProps) => {
	const {
		emailValidation,
		invalidEmailLabel,
		selectedChips,
		placeholder,
		onValueChange,
		...other
	} = props
	const [inputValue, setInputValue] = useState<string>('')
	const [selectedItem, setSelectedItem] = useState<readonly string[]>([])
	useEffect(() => {
		selectedChips(selectedItem)
	}, [selectedItem, selectedChips])

	const cleanInputValue = inputValue.trim()

	useEffect(() => {
		if (onValueChange) {
			onValueChange(cleanInputValue)
		}
	}, [cleanInputValue, onValueChange])

	// TODO(pascal): Cleanup duplication, add tests
	const handlePaste = useCallback(
		(event: React.ChangeEvent<HTMLDivElement>) => {
			const { value } = event.target as HTMLInputElement
			const values = value.trim().split(/,+|;+|\s+/i)

			const newSelectedItem = [...selectedItem]

			for (const address of values) {
				const duplicatedValues = newSelectedItem.indexOf(address)

				if (duplicatedValues !== -1) {
					continue
				}

				if (address.replaceAll(/\s/g, '').length === 0) {
					continue
				}

				if (emailValidation) {
					if (isValidEmail(address)) {
						setInputValue(address)
					} else {
						newSelectedItem.push(address)
					}
				}
			}

			setSelectedItem(newSelectedItem)
		},
		[selectedItem, emailValidation]
	)

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			if (
				event.key === 'Enter' ||
				event.key === ' ' ||
				event.key === ',' ||
				event.key === ';'
			) {
				let { value } = event.target as HTMLInputElement
				value = value.trim()
				const newSelectedItem = [...selectedItem]

				const duplicatedValues = newSelectedItem.indexOf(value)

				if (duplicatedValues !== -1) {
					setInputValue('')
					return
				}
				if (value.replaceAll(/\s/g, '').length === 0) {
					return
				}

				if (emailValidation && !isValidEmail(value)) {
					return
				}

				newSelectedItem.push(value)
				setSelectedItem(newSelectedItem)
				setInputValue('')
			}

			if (
				selectedItem.length > 0 &&
				inputValue.length === 0 &&
				event.key === 'Backspace'
			) {
				setSelectedItem(selectedItem.slice(0, selectedItem.length - 1))
			}
		},
		[emailValidation, inputValue.length, selectedItem]
	)

	const handleChange = useCallback(
		(item: string) => {
			let newSelectedItem = [...selectedItem]
			if (newSelectedItem.indexOf(item) === -1) {
				newSelectedItem = [...newSelectedItem, item]
			}
			setInputValue('')
			setSelectedItem(newSelectedItem)
		},
		[selectedItem]
	)

	const handleDelete = useCallback(
		(item: string) => {
			const newSelectedItem = [...selectedItem]
			newSelectedItem.splice(newSelectedItem.indexOf(item), 1)
			setSelectedItem(newSelectedItem)
		},
		[selectedItem]
	)

	const handleInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { value } = event.target
			if (value.length - inputValue.length > 1) {
				handlePaste(event)
			} else {
				if (value === ',' || value === ';') {
					return
				}
				setInputValue(value)
			}
		},
		[inputValue, handlePaste]
	)

	const isEmailInvalidErrorShown = !!(
		cleanInputValue &&
		emailValidation &&
		!isValidEmail(cleanInputValue)
	)

	const handleInputPropsChange = useCallback(
		(
			event: React.ChangeEvent<HTMLInputElement>,
			onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
		) => {
			handleInputChange(event)
			onChange?.(event)
		},
		[handleInputChange]
	)

	const renderDownshift = useCallback(
		({ getInputProps, isOpen }: ControllerStateAndHelpers<unknown>) => {
			const { onBlur, onChange, ...inputProps } = getInputProps({
				onKeyDown: handleKeyDown,
				placeholder,
			})
			return (
				<div>
					<ChipsInputContainerWithEvents
						multiline
						onBlur={onBlur}
						onChange={onChange}
						onDelete={handleDelete}
						selectedItems={selectedItem}
						onInputPropsChange={handleInputPropsChange}
						inputProps={inputProps}
						error={!isOpen && isEmailInvalidErrorShown}
						helperText={
							!isOpen && isEmailInvalidErrorShown
								? invalidEmailLabel
								: undefined
						}
						{...other}
					/>
				</div>
			)
		},
		[
			handleDelete,
			handleInputPropsChange,
			handleKeyDown,
			invalidEmailLabel,
			other,
			placeholder,
			selectedItem,
			isEmailInvalidErrorShown,
		]
	)

	return (
		<>
			{/* @ts-expect-error */}
			<Downshift
				id="downshift-multiple"
				inputValue={inputValue}
				onChange={handleChange}
				selectedItem={selectedItem}
			>
				{renderDownshift}
			</Downshift>
		</>
	)
}

export { ChipsInput }
